/* eslint-disable max-statements */
import { Button } from '@design-system/button';
import { FluidGrid } from '@design-system/fluid-grid';
import { PageBlock } from '@design-system/page-block';
import { Spacer } from '@design-system/spacer';
import React, { forwardRef, useRef, useState } from 'react';

import { executeSearch } from '~/components/SearchResults/SearchService/executeSearch';
import { useLoadMore } from '~/hooks/useLoadMore';

import { normalizeItems } from '../ItemListingUtils';
import { ItemListingLoadMoreProps } from './models';

export const ItemListingLoadMore = forwardRef<
  HTMLDivElement,
  Omit<ItemListingLoadMoreProps, 'type'>
>(
  (
    {
      pageBlock,
      fluidGrid,
      items: articles,
      rawItems,
      loadMoreButton,
      initialNumberArticles = 8,
      id,
      uid,
      onItemsLoaded,
      content_type,
      typeName,
      locale = 'en-au',
      allLoaded,
      onAllItemsLoaded,
      selectEvents,
    },
    ref
  ) => {
    const [fetched, setFetched] = useState(false);
    const fetchingRef = useRef(false);
    const hasTriggeredFetchRef = useRef(false);

    const { nextPage, visibleItems, nextPageButtonVisible } = useLoadMore({
      items: articles,
      itemsPerPage: initialNumberArticles,
    });

    const searchDataType =
      content_type === 'l2_event_page' ? 'event' : 'resource';

    const fetchAllAdditionalItems = async () => {
      if (
        allLoaded ||
        hasTriggeredFetchRef.current ||
        fetched ||
        fetchingRef.current
      )
        return [];

      if (!['event', 'resource'].includes(searchDataType)) return [];

      hasTriggeredFetchRef.current = true;
      fetchingRef.current = true;

      try {
        const searchResultsData = await executeSearch(searchDataType, locale);

        const searchResultsDataByType = searchResultsData[`${searchDataType}s`];

        const normalizedItems = normalizeItems(searchResultsDataByType);

        let processedItems = normalizedItems;

        if (searchDataType === 'event') {
          const now = Date.now();

          if (selectEvents === 'Past') {
            processedItems = normalizedItems.filter((item) => {
              const eventDate = new Date(
                item.eventTimestamp.event_start_date ?? 0
              ).getTime();

              return eventDate < now;
            });
          }

          if (selectEvents === 'Future') {
            processedItems = normalizedItems.filter((item) => {
              const eventDate = new Date(
                item.eventTimestamp.event_start_date ?? 0
              ).getTime();

              return eventDate > now;
            });
          }
        }

        const sortedItems = processedItems.sort((a, b) => {
          const getDate = (item) =>
            searchDataType === 'event'
              ? new Date(item.eventTimestamp.event_start_date ?? 0).getTime()
              : new Date(item.pageAdditionalData.initial_publish_date ?? 0).getTime();

          return getDate(b) - getDate(a);
        });

        const existingFullPaths = new Set(
          (rawItems?.items || [])
            .map((item) => item.fullPath)
            .filter(Boolean)
        );

        const seenNewFullPaths = new Set();

        const additionalItems = sortedItems.filter((item) => {
          const { fullPath } = item;

          if (!fullPath) return true;
          if (existingFullPaths.has(fullPath)) return false;
          if (seenNewFullPaths.has(fullPath)) return false;

          seenNewFullPaths.add(fullPath);

          return true;
        });

        onItemsLoaded?.(additionalItems);
        onAllItemsLoaded?.();

        setFetched(true);

        return additionalItems;
      } catch (error) {
        hasTriggeredFetchRef.current = false;
        throw error;
      } finally {
        fetchingRef.current = false;
      }
    };

    const onLoadMore = async () => {
      if (typeName?.includes('all_item_listing_grid') && !allLoaded) {
        await fetchAllAdditionalItems();
      }

      nextPage();
    };

    return (
      <PageBlock
        {...pageBlock}
        ref={ref}
        className="load-more-block"
        id={id}
        componentUid={uid}
      >
        <div className="load-more-block__container">
          {visibleItems.length ? (
            <>
              <FluidGrid {...fluidGrid}>{visibleItems}</FluidGrid>
              <Spacer size={7} />
              {nextPageButtonVisible &&
                loadMoreButton?.children &&
                initialNumberArticles ? (
                <div className="load-more-block__button-container">
                  <Button
                    {...loadMoreButton}
                    onClick={onLoadMore}
                    className="load-more-block__button"
                  >
                    {loadMoreButton.children}
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </PageBlock>
    );
  }
);

export default ItemListingLoadMore;
